'use client';
import 'mapbox-gl/dist/mapbox-gl.css';

import type { Address, Team } from '@baseball-near-you/prisma';
import { env } from '@bb/env';
import { useUserPositionContext } from '@bb/lib/contexts/UserPosition';
import type { MotionValue } from 'framer-motion';
import type React from 'react';
import { type Dispatch, Fragment, useEffect, useRef } from 'react';
import Mapbox, { type MapRef, Marker, type Point, Popup } from 'react-map-gl';
import {
  type MapBounds,
  TEAM_MENU_OPEN_ZOOM,
  getIsTeamMenuOpen,
} from './LeagueMapContainer';
import { LeagueMapTeam } from './LeagueMapTeam';

interface MapboxLeagueMapProps {
  currentTeam: (Team & { address: Address[]; distance?: number }) | null;
  teams?: (Team & { address: Address[] })[] | null;
  setCurrentTeam: (
    team: (Team & { distance?: number; address: Address[] }) | null
  ) => void;
  teamMenuContainer: HTMLDivElement | null;
  mapBounds: MapBounds | null;
  setMapBounds: Dispatch<React.SetStateAction<MapBounds | null>>;
  startingCoords?: { lat: number; lng: number };
  viewState: {
    zoom: number;
    longitude: number;
    latitude: number;
  };
  setViewState: Dispatch<
    React.SetStateAction<{ zoom: number; longitude: number; latitude: number }>
  >;
  isTeamMenuOpen: boolean;
  clusters?: {
    clusterLat: number;
    clusterLng: number;
    teamCount: number;
    representativeTeamId: string;
  }[];
  setIsTeamMenuOpen: Dispatch<React.SetStateAction<boolean>>;
  menuWidth: MotionValue<number>;
}

export default function MapboxLeagueMap(props: MapboxLeagueMapProps) {
  const { userPosition } = useUserPositionContext();
  const mapRef = useRef<MapRef>(null);

  // TODO might wanna memoize the markers
  // TODO so that we dont render markers that are already rendered
  // const markersMapRed = useRef<
  //   Record<
  //     // ke
  //     string,
  //     string
  //   >
  // >({});

  useEffect(() => {
    if (userPosition.long && userPosition.lat && !props.startingCoords) {
      props.setViewState({
        longitude: userPosition.long,
        latitude: userPosition.lat,
        zoom: props.viewState.zoom,
      });
    }
  }, [userPosition?.lat, userPosition?.long, props.startingCoords]);

  // move to a clicked team from the sidebar menu
  useEffect(() => {
    if (
      props.currentTeam?.address?.[0].lat &&
      props.currentTeam?.address?.[0].lng
    ) {
      const { lat, lng } = props.currentTeam.address[0];
      props.setViewState({
        longitude: lng,
        latitude: lat,
        zoom: props.viewState.zoom ?? 9,
      });
    }
  }, [props.currentTeam?.address?.[0]]);

  return (
    <Mapbox
      {...props.viewState}
      ref={mapRef}
      onMove={(e) => {
        props.setViewState(e.viewState);
        const isOpen = getIsTeamMenuOpen(e.viewState.zoom);
        if (props.isTeamMenuOpen !== isOpen) {
          props.setIsTeamMenuOpen(isOpen);
          props.menuWidth.set(isOpen ? 375 : 0);
          if (!isOpen) {
            props.setCurrentTeam(null);
          }
        }
      }}
      onMoveEnd={(e) => {
        const bounds = e.target.getBounds();
        const swBounds = bounds.getSouthWest();
        const pixelValue = e.target.project(swBounds);
        const adjustedPixelValue = !getIsTeamMenuOpen(e.viewState.zoom)
          ? ({ ...pixelValue, x: pixelValue.x - 375 } as Point)
          : (pixelValue as Point);
        const adjustedSwBounds = e.target.unproject(adjustedPixelValue);

        props.setMapBounds({
          ne: bounds.getNorthEast(),
          sw: adjustedSwBounds,
        });
      }}
      mapboxAccessToken={env.NEXT_PUBLIC_MAPBOX_TOKEN}
      initialViewState={{
        longitude: -122.4,
        latitude: 37.8,
        zoom: 14,
      }}
      style={{ width: '100%', height: '100%' }}
      mapStyle="mapbox://styles/mapbox/streets-v9"
      padding={{
        left: 375 + 6,
        right: 6,
        top: 6,
        bottom: 6,
      }}
    >
      {props.isTeamMenuOpen
        ? props.teams?.map((team) => {
            const isMarkerActive =
              props.currentTeam && props.currentTeam.id === team.id;

            const { lat, lng } = team.address?.[0] || { lat: null, lng: null };

            return lat !== null && lng !== null ? (
              <Fragment
                key={`${team.slug}-${team.id}-${team.name}-marker-popup-fragment`}
              >
                {isMarkerActive && (
                  <Popup
                    key={`${team.slug}-${team.id}popup`}
                    latitude={lat}
                    longitude={lng}
                    closeButton={false}
                    closeOnClick={false}
                    anchor="bottom"
                    offset={35}
                    maxWidth="1000px"
                    focusAfterOpen={false}
                  >
                    <LeagueMapTeam
                      team={team}
                      isCurrentTeam={false}
                      setCurrentTeam={() => {}}
                      markerPreview
                    />
                  </Popup>
                )}
                <TeamMarker
                  team={team}
                  currentTeam={props.currentTeam}
                  setCurrentTeam={props.setCurrentTeam}
                  mapRef={mapRef}
                  viewState={props.viewState}
                />
              </Fragment>
            ) : null;
          })
        : props.clusters &&
          props.clusters.length > 0 &&
          props.clusters.map((cluster) => {
            const size =
              cluster.teamCount * 1.6 * (props.viewState.zoom / 10) + 25;
            return (
              <Fragment
                key={`cluster-${cluster.representativeTeamId}-${cluster.clusterLat}-${cluster.clusterLng}`}
              >
                <Marker
                  key={`${cluster.representativeTeamId}-${cluster.clusterLat}-${cluster.clusterLng}-hiddenmarker`}
                  latitude={cluster.clusterLat}
                  longitude={cluster.clusterLng}
                  color={'#E72425'}
                  style={{
                    opacity: 0,
                  }}
                  onClick={() => {
                    console.log(cluster);
                  }}
                  scale={1}
                />
                <Marker
                  key={`${cluster.representativeTeamId}-${cluster.clusterLat}-${cluster.clusterLng}-custommarker`}
                  latitude={cluster.clusterLat}
                  longitude={cluster.clusterLng}
                  onClick={() => {
                    if (!mapRef.current) return;
                    const zoom =
                      props.viewState.zoom + 2 > TEAM_MENU_OPEN_ZOOM
                        ? TEAM_MENU_OPEN_ZOOM
                        : props.viewState.zoom + 2;

                    const pixelValue = mapRef.current.project([
                      cluster.clusterLng,
                      cluster.clusterLat,
                    ]);

                    const adjustedPixelValue = {
                      x: pixelValue.x, // window.devicePixelRatio + 375 / 2,
                      // (mapRef.current.getCanvas().width - pixelValue.x) /
                      // window.devicePixelRatio,
                      y: pixelValue.y,
                    } as Point;
                    console.log(
                      mapRef.current.getCanvas().width,
                      pixelValue.x,
                      adjustedPixelValue
                    );
                    const adjustedBounds =
                      mapRef.current.unproject(adjustedPixelValue);

                    mapRef.current?.flyTo({
                      center: [adjustedBounds.lng, adjustedBounds.lat],
                      zoom,
                    });
                  }}
                  scale={1}
                >
                  <div
                    className="flex cursor-pointer items-center justify-center rounded-full border-white bg-[radial-gradient(circle_at_50%,#E72425,#9C1919)] font-sans text-white"
                    style={{
                      width: `${size}px`,
                      height: `${size}px`,
                      fontSize: `${size / 2.75 > 12 ? size / 2.75 : 12}px`,
                    }}
                  >
                    {cluster.teamCount}
                  </div>
                </Marker>
              </Fragment>
            );
          })}
    </Mapbox>
  );
}

export function TeamMarker({
  team,
  currentTeam,
  setCurrentTeam,
  mapRef,
  viewState,
}: {
  team: Team & { address: Address[] };
  currentTeam: (Team & { address: Address[] }) | null;
  setCurrentTeam: (team: Team & { address: Address[] }) => void;
  mapRef: React.RefObject<MapRef | null>;
  viewState: { zoom: number; longitude: number; latitude: number };
}) {
  const isMarkerActive = currentTeam && currentTeam.id === team.id;

  const { lat, lng } = team.address?.[0] || { lat: null, lng: null };

  return lat !== null && lng !== null ? (
    <Marker
      key={`${team.slug}-${team.id}-marker`}
      latitude={lat}
      longitude={lng}
      color={isMarkerActive ? 'lightgray' : '#E72425'}
      onClick={(e) => {
        console.log(team.id, currentTeam?.id);
        setCurrentTeam(team);
        const latLong = e.target.getLngLat();
        mapRef.current?.easeTo({
          center: [latLong.lng, latLong.lat],
          zoom: viewState.zoom,
        });
      }}
      style={{
        cursor: 'pointer',
        zIndex: isMarkerActive ? 1 : 0,
        color: isMarkerActive ? 'gray' : '#E72425',
      }}
      scale={isMarkerActive ? 1.25 : 1}
    />
  ) : null;
}
